import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { message, UploadFile, } from 'antd';
import DocumentPreview from "./document-preview";
import server from "../../../server";
import { useBaseComponent } from "../../../base/base-component";
import { UploadChangeParam } from "antd/es/upload";
import { fetchDocument } from "./action/document.action";
import { IDocument, IDocumentsProps, IState } from "./type/document.types";
import DocumentUploadTrigger from "./utils/document-upload-trigger.view";
import DocumentView from "./utils/document-view.view";
import { MetaPayload } from "../../../domains/models/document";
import "../../../styles/component/document/document.scss";

const DocumentUploader = forwardRef((props: IDocumentsProps, ref) => {
    const {state, updateState} = useBaseComponent<IDocumentsProps, IState>(props, {
        initialState: () => ({
            document: {
                id: '',
                name: '',
                contentType: '',
            },
            showPreview: false,
            selectedFile: null,

        }),
        onInit: () => onInit()
    });

    useEffect(() => {
        (async () => {
            if (props.value) {
                let document: IDocument = await fetchDocument(props.value);
                updateState('document', document);
            } else {
                updateState('document', {
                    id: '',
                    name: '',
                    contentType: '',
                });
            }
        })();
    }, [props.value]);

    useImperativeHandle(ref, () => ({
        uploadDocument: uploadDocument,
        hasFile: () => !!state.selectedFile || !!state.document.id,
        documentId: state.document.id,
    }));

    async function onInit() {
        if (props.value) {
            let document: IDocument = await fetchDocument(props.value);
            updateState('document', document);
        }
    }

    const handleFileSelect = (file: File) => {
        updateState('selectedFile', file);
    };

    const handleDocumentUpload = async (info: UploadChangeParam<UploadFile<any>>) => {

        if (props.uploadMode === 'onSubmit') {
            const fileObj = info.file as unknown as File;
            if (fileObj) {
                handleFileSelect(fileObj);
            }
            return;
        } else {
            try {
                const response: any = await server.document.upload(info.file, props.docType, props.docNumber, props.employeeId);
                const doc = response[0];
                const newDocument: IDocument = {
                    id: doc.id,
                    name: doc.originalFileName,
                    contentType: doc.contentType,
                };
                updateState('document', newDocument);
                props.onChange?.(doc.id);
                props.onUploadSuccess?.(doc);
                message.success("Document uploaded successfully");
            } catch (error: any) {
                message.error('Failed to upload document');
                console.log('Failed to upload document', error)
            }
        }



    };

    const uploadDocument = async (meta?: MetaPayload[]) => {
        // If already uploaded → reuse
        if (state.document.id) {
            return state.document.id;
        }

        if (!state.selectedFile) {
            message.error("No file selected");
            return '';
        }

        try {


            const response: any = await server.document.upload(
                state.selectedFile,
                props.docType,
                props.docNumber,
                props.employeeId,
                meta
            );

            const doc = response[0];

            const newDocument: IDocument = {
                id: doc.id,
                name: doc.originalFileName,
                contentType: doc.contentType,
            };

            updateState('document', newDocument);
            props.onChange?.(doc.id);


            return doc.id;
        } catch (e) {
            message.error("Upload failed");
            return '';
        }

    };


    const handleDocumentDelete = async (event: any) => {
        event.stopPropagation();

        if (state.selectedFile && !state.document.id) {
            updateState('selectedFile', null);
            message.success("Removed selected file");
            return;
        }

        if (state.document.id) {
            await server.document.delete(state.document.id);

            updateState('document', {
                id: '',
                name: '',
                contentType: '',
            });

            props.onChange?.('');
            message.success("Document deleted successfully");
            return;
        }
    };

    function openPreview(event: any) {
        event.stopPropagation();
        updateState('showPreview', true);
    }

    function closePreview() {
        updateState('showPreview', false);
    }

    const hasFile = state.document.id || state.selectedFile;

    return !hasFile ? (
        <DocumentUploadTrigger
            disabled={props.disabled}
            handleDocumentUpload={handleDocumentUpload}
            docType={props.docType}
            uploadButtonText={props.uploadButtonText}

        />
    ) : (
        <>
            <DocumentView
                docId={state.document.id || ''}
                docName={
                    state.document.name ||
                    state.selectedFile?.name ||
                    ''
                }
                openPreview={openPreview}
                deleteDocument={handleDocumentDelete}
            />
            <DocumentPreview
                document={state.document}
                showPreview={state.showPreview}
                closePreview={closePreview}
                localFile={state.selectedFile}

            />
        </>

    );
});

export default DocumentUploader;
